/* eslint-disable jsx-a11y/no-static-element-interactions */
import { SyntheticEvent, useMemo, useRef } from 'react';

import { InputHelper, InputLabel, Theme } from '@lumx/react';
import { useEventCallback } from '@lumx/react/hooks/useEventCallback';
import { GenericProps, HasTheme } from '@lumx/react/utils/type';
import type { LumxClassName } from '@lumx/core/js/types';
import { classNames } from '@lumx/core/js/utils';
import { clamp } from '@lumx/react/utils/number/clamp';
import { useId } from '@lumx/react/hooks/useId';
import { useTheme } from '@lumx/react/utils/theme/ThemeContext';
import { forwardRef } from '@lumx/react/utils/react/forwardRef';
import { useDisableStateProps } from '@lumx/react/utils/disabled/useDisableStateProps';

/**
 * Defines the props of the component.
 */
export interface SliderProps extends GenericProps, HasTheme {
    /** Helper text. */
    helper?: string;
    /** Whether the min and max labels should be hidden or not. */
    hideMinMaxLabel?: boolean;
    /** Whether the component is disabled or not. */
    isDisabled?: boolean;
    /** Label text. */
    label?: string;
    /** Maximum value on the range. */
    max: number;
    /** Minimum value of the range. */
    min: number;
    /** Native input name property. */
    name?: string;
    /** Number of digits in the fractional part of the selected value. */
    precision?: number;
    /** Range step value. */
    steps?: number;
    /** Selected ranged value. */
    value: number;
    /** On change callback. */
    onChange(value: number, name?: string, event?: SyntheticEvent): void;
    /** On click callback. */
    onMouseDown?(event: React.SyntheticEvent): void;
}

/**
 * Component display name.
 */
const COMPONENT_NAME = 'Slider';

/**
 * Component default class name and class prefix.
 */
const CLASSNAME: LumxClassName<typeof COMPONENT_NAME> = 'lumx-slider';
const { block, element } = classNames.bem(CLASSNAME);

/**
 * Component default props.
 */
const DEFAULT_PROPS: Partial<SliderProps> = {
    precision: 0,
    steps: 0,
};

/**
 * Convert a percent value to a value in range min - max.
 *
 * @param percent   Value to convert.
 * @param min       Minimum value.
 * @param max       Maximum value.
 * @param precision Precision.
 * @return Value in range min - max
 */
const computeValueFromPercent = (percent: number, min: number, max: number, precision = 0): number =>
    Number((min + percent * (max - min)).toFixed(precision));

/**
 * Convert a value in range min - max to a percent value.
 *
 * @param value Value to convert.
 * @param min   Minimum value.
 * @param max   Maximum value.
 * @return Value in percent
 */
const computePercentFromValue = (value: number, min: number, max: number): number =>
    Number((value - min) / (max - min));

/**
 * Type guard checking whether an event is a touch event (native or React synthetic).
 *
 * @param event The interaction event.
 * @return Whether the event is a touch event.
 */
const isTouchEvent = (
    event: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent,
): event is React.TouchEvent | TouchEvent => 'touches' in event;

/**
 * Read the horizontal page position from either a mouse or touch event.
 *
 * @param event The interaction event.
 * @return The page X position.
 */
const getPageX = (event: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent): number => {
    if (isTouchEvent(event)) {
        return (event.touches[0] ?? event.changedTouches[0]).pageX;
    }
    return event.pageX;
};

/**
 * Slider component.
 *
 * @param  props Component props.
 * @param  ref   Component ref.
 * @return React element.
 */
export const Slider = forwardRef<SliderProps, HTMLDivElement>((props, ref) => {
    const { isAnyDisabled, disabledStateProps, otherProps } = useDisableStateProps(props);
    const defaultTheme = useTheme() || Theme.light;
    const {
        className,
        helper,
        hideMinMaxLabel,
        id,
        label,
        max,
        min,
        name,
        onChange,
        onMouseDown,
        precision = DEFAULT_PROPS.precision,
        steps = DEFAULT_PROPS.steps,
        theme = defaultTheme,
        value,
        ...forwardedProps
    } = otherProps;
    const generatedId = useId();
    const sliderId = id || generatedId;
    const sliderLabelId = useMemo(() => `label-${sliderId}`, [sliderId]);
    const sliderRef = useRef<HTMLDivElement>(null);

    // build a lookup array for the steps.
    const availableSteps = useMemo((): number[] => {
        if (!steps) return [];

        const available = [0];
        const percentStep = 1 / ((max - min) / steps);
        let ptr = 0;
        while (ptr + percentStep <= 1) {
            ptr += percentStep;
            available.push(ptr);
        }
        return available;
    }, [steps, min, max]);

    /**
     * Try to find the closest step to the current slider position.
     *
     * @param percentValue Reference value
     * @return The closest step value
     */
    const findClosestStep = (percentValue: number): number => {
        const closest = availableSteps.reduce(
            (acc, step) => {
                const aDst = Math.abs(percentValue - step);
                if (aDst < acc.dst) {
                    return { dst: aDst, val: step };
                }
                return acc;
            },
            { dst: Infinity, val: -1 },
        );
        return closest.val;
    };

    /**
     * Convert slider's handle position to percent.
     *
     * @param event The interaction event
     * @param slider the slider element
     * @return The computed percent value
     */
    const getPercentValue = (
        event: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent,
        slider: HTMLDivElement,
    ): number => {
        const { width, left } = slider.getBoundingClientRect();
        let percent = (getPageX(event) - left - window.pageXOffset) / width;
        percent = clamp(percent, 0, 1);
        if (steps) {
            percent = findClosestStep(percent);
        }
        return percent;
    };

    /**
     * Register a handler for the mouse/touch move event.
     */
    const handleMove = useEventCallback((event: MouseEvent | TouchEvent) => {
        const { current: slider } = sliderRef;
        if (!slider || !onChange) return;
        if (isTouchEvent(event)) {
            event.preventDefault();
        }
        const newValue = getPercentValue(event, slider);
        onChange(computeValueFromPercent(newValue, min, max, precision), name, event as unknown as SyntheticEvent);
    });

    /**
     * Register a handler for the mouse up/touch end/touch cancel event.
     * Clean a all listeners.
     */
    const handleEnd = useEventCallback(() => {
        document.body.removeEventListener('mousemove', handleMove as any);
        document.body.removeEventListener('mouseup', handleEnd);
        document.body.removeEventListener('touchmove', handleMove as any);
        document.body.removeEventListener('touchend', handleEnd);
        document.body.removeEventListener('touchcancel', handleEnd);
    });

    /**
     * Move to the next or previous value (i.e. + or - 10%) or next step
     * @param previous Should seek the previous value.
     */
    const hopToValue = (previous = false) => {
        const oldPercent = computePercentFromValue(value, min, max);
        let percent = clamp(oldPercent + (previous ? -0.1 : 0.1), 0, 1);
        if (steps) {
            percent = oldPercent + availableSteps[1] * (previous ? -1 : 1);
            percent = findClosestStep(percent);
        }
        if (onChange) {
            onChange(computeValueFromPercent(percent, min, max, precision), name);
        }
    };

    /**
     * Register a handler for keyboard interactions
     */
    const handleKeyDown = useEventCallback((event: React.KeyboardEvent) => {
        if (event.key === 'ArrowRight') {
            hopToValue();
        } else if (event.key === 'ArrowLeft') {
            hopToValue(true);
        }
    });

    /**
     * Register a handler for the mouseDown event.
     */
    const handleMouseDown = useEventCallback((event: React.MouseEvent) => {
        onMouseDown?.(event);

        const { current: slider } = sliderRef;
        if (isAnyDisabled || !slider) return;
        const newValue = getPercentValue(event, slider);
        if (onChange) {
            onChange(computeValueFromPercent(newValue, min, max, precision), name, event);
        }

        document.body.addEventListener('mousemove', handleMove as any);
        document.body.addEventListener('mouseup', handleEnd);
    });

    /**
     * Register a handler for the touchStart event.
     */
    const handleTouchStart = useEventCallback((event: React.TouchEvent) => {
        const { current: slider } = sliderRef;
        if (isAnyDisabled || !slider) return;
        const newValue = getPercentValue(event, slider);
        if (onChange) {
            onChange(computeValueFromPercent(newValue, min, max, precision), name, event);
        }

        document.body.addEventListener('touchmove', handleMove as any, { passive: false });
        document.body.addEventListener('touchend', handleEnd);
        document.body.addEventListener('touchcancel', handleEnd);
    });

    const percentString = `${computePercentFromValue(value, min, max) * 100}%`;
    return (
        <div
            ref={ref}
            {...forwardedProps}
            className={classNames.join(
                className,
                block({
                    [`theme-${theme}`]: Boolean(theme),
                    'has-label': Boolean(label),
                }),
            )}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            {label && (
                <InputLabel id={sliderLabelId} htmlFor={sliderId} className={element('label')} theme={theme}>
                    {label}
                </InputLabel>
            )}

            {helper && (
                <InputHelper className={element('helper')} theme={theme}>
                    {helper}
                </InputHelper>
            )}

            <div className={element('ui-wrapper')}>
                {!hideMinMaxLabel && <span className={element('value-label', { min: true })}>{min}</span>}
                <div className={element('wrapper')} ref={sliderRef}>
                    <div
                        className={element('track', {
                            background: true,
                        })}
                    />
                    <div
                        className={element('track', {
                            active: true,
                        })}
                        style={{ width: percentString }}
                    />
                    {steps ? (
                        <div className={element('ticks')}>
                            {availableSteps.map((step, idx) => (
                                <div
                                    key={`tick_${idx}`}
                                    className={element('tick')}
                                    style={{ left: `${step * 100}%` }}
                                />
                            ))}
                        </div>
                    ) : null}
                    <button
                        type="button"
                        aria-labelledby={sliderLabelId}
                        name={name}
                        id={sliderId}
                        className={element('handle')}
                        style={{ left: percentString }}
                        onKeyDown={isAnyDisabled ? undefined : handleKeyDown}
                        {...disabledStateProps}
                    />
                </div>
                {!hideMinMaxLabel && <span className={element('value-label', { max: true })}>{max}</span>}
            </div>
        </div>
    );
});
Slider.displayName = COMPONENT_NAME;
Slider.className = CLASSNAME;
Slider.defaultProps = DEFAULT_PROPS;
